/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import type { CustomerAppointmentRow, CustomerAppointmentSummary } from '../screens/hooks/useCustomerAppointments';

type BranchOption = { id: string; name: string };

type Props = {
  appointments: CustomerAppointmentRow[];
  loading: boolean;
  summary: CustomerAppointmentSummary;
  totalCount?: number;
  apptPage?: number;
  apptHasMore?: boolean;
  onLoadApptPage?: (page: number) => void;
  branches?: BranchOption[];
  mobile?: boolean;
  onViewSale?: (saleId: string) => void;
};

const STATUS_LABEL: Record<CustomerAppointmentRow['status'], string> = {
  requested: 'Solicitado',
  confirmed: 'Confirmado',
  completed: 'Realizado',
  cancelled: 'Cancelado',
  no_show: 'No asistió',
};

const STATUS_COLOR: Record<CustomerAppointmentRow['status'], string> = {
  requested: ticketColors.ink3,
  confirmed: '#B45309',
  completed: ticketColors.ok,
  cancelled: ticketColors.bad,
  no_show: ticketColors.bad,
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

const PAYMENT_MODE_LABEL: Record<string, string> = {
  none: 'Sin seña',
  deposit: 'Con seña',
  full: 'Pago completo',
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  partial: 'Parcial',
  refunded: 'Reembolsado',
  unpaid: 'Sin pagar',
};

function formatDuration(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const mins = Math.round((end.getTime() - start.getTime()) / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

export function CustomerAppointmentHistorySection({
  appointments,
  loading,
  summary,
  totalCount,
  apptPage = 0,
  apptHasMore = false,
  onLoadApptPage,
  branches,
  mobile = false,
  onViewSale,
}: Props) {
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const hasAppointments = appointments.length > 0;

  // Branches que aparecen en los turnos del cliente (intersect con branches disponibles).
  const availableBranches = useMemo<BranchOption[]>(() => {
    if (!branches || branches.length === 0) return [];
    const branchIdsInAppts = new Set(appointments.map((a) => a.branchId).filter(Boolean));
    if (branchIdsInAppts.size <= 1) return [];
    return branches.filter((b) => branchIdsInAppts.has(b.id));
  }, [appointments, branches]);

  const showBranchFilter = availableBranches.length >= 2;

  const filteredAppointments = useMemo(() => {
    if (!branchFilter) return appointments;
    return appointments.filter((a) => a.branchId === branchFilter);
  }, [appointments, branchFilter]);
  const showSummary =
    summary.totalVisits > 0 ||
    summary.nextAppointmentAt != null ||
    summary.cancelledCount > 0 ||
    summary.noShowCount > 0 ||
    summary.daysSinceLastVisit != null ||
    summary.avgDaysBetweenVisits != null;

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.appointments"
    >
      <View style={styles.headerRow}>
        <Ionicons name="calendar-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.title}>Turnos</Text>
        {totalCount != null && totalCount > 0 ? (
          <View style={styles.countBadge}>
            <Text style={styles.countText}>{totalCount}</Text>
          </View>
        ) : null}
      </View>

      {showSummary ? (
        <View style={styles.summaryBox} testID="customers.detail.appointments-summary">
          {summary.totalVisits > 0 ? (
            <Text style={styles.summaryLine}>
              <Text style={styles.summaryBold}>{summary.totalVisits}</Text>
              {summary.totalVisits === 1 ? ' visita' : ' visitas'}
              {summary.lastVisitAt ? ` · última ${formatShortDate(summary.lastVisitAt)}` : ''}
              {summary.daysSinceLastVisit != null ? ` · hace ${summary.daysSinceLastVisit}d` : ''}
              {summary.avgDaysBetweenVisits != null ? ` · cada ${summary.avgDaysBetweenVisits}d` : ''}
            </Text>
          ) : null}
          {summary.nextAppointmentAt ? (
            <Text style={[styles.summaryLine, { color: '#B45309' }]}>
              Próximo: {formatShortDate(summary.nextAppointmentAt)}
            </Text>
          ) : null}
          {summary.noShowCount > 0 || summary.cancelledCount > 0 ? (
            <Text style={styles.summaryLine}>
              {summary.noShowCount > 0 ? (
                <Text>
                  <Text style={{ color: ticketColors.bad }}>{summary.noShowCount}</Text> no asistió
                </Text>
              ) : null}
              {summary.noShowCount > 0 && summary.cancelledCount > 0 ? ' · ' : ''}
              {summary.cancelledCount > 0 ? (
                <Text>
                  <Text style={{ color: ticketColors.ink3 }}>{summary.cancelledCount}</Text> cancelado{summary.cancelledCount === 1 ? '' : 's'}
                </Text>
              ) : null}
              {(summary.noShowCount > 0 || summary.cancelledCount > 0) && summary.totalVisits > 0 ? (
                <Text style={styles.summaryMuted}>
                  {'  ·  '}{Math.round(((summary.noShowCount + summary.cancelledCount) / (summary.totalVisits + summary.noShowCount + summary.cancelledCount)) * 100)}% de pérdida
                </Text>
              ) : null}
            </Text>
          ) : null}
        </View>
      ) : null}

      {loading ? (
        <Text style={styles.emptyText}>Cargando turnos…</Text>
      ) : !hasAppointments ? (
        <Text style={styles.emptyText}>Sin turnos registrados.</Text>
      ) : (
        <View style={styles.list}>
          {showBranchFilter ? (
            <View style={styles.branchFilterRow} testID="customers.detail.branch-filter">
              <TouchableOpacity
                style={[styles.branchChip, !branchFilter && styles.branchChipActive]}
                onPress={() => setBranchFilter(null)}
                accessibilityRole="button"
                accessibilityLabel="Todas las sucursales"
              >
                <Text style={[styles.branchChipText, !branchFilter && styles.branchChipTextActive]}>
                  Todas
                </Text>
              </TouchableOpacity>
              {availableBranches.map((b) => (
                <TouchableOpacity
                  key={b.id}
                  style={[styles.branchChip, branchFilter === b.id && styles.branchChipActive]}
                  onPress={() => setBranchFilter(b.id)}
                  accessibilityRole="button"
                  accessibilityLabel={`Filtrar por ${b.name}`}
                >
                  <Text
                    style={[styles.branchChipText, branchFilter === b.id && styles.branchChipTextActive]}
                    numberOfLines={1}
                  >
                    {b.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
          {filteredAppointments.map((a) => {
            const isExpanded = expandedId === a.id;
            const hasDetail = a.resourceName || a.notes || a.paymentMode || a.saleId;
            return (
              <View key={a.id} style={styles.rowContainer} testID={`customers.detail.appointment.${a.id}`}>
                <TouchableOpacity
                  style={styles.row}
                  onPress={() => hasDetail ? setExpandedId(isExpanded ? null : a.id) : undefined}
                  disabled={!hasDetail}
                  accessibilityRole="button"
                  accessibilityLabel={`Turno ${a.serviceName} ${formatDate(a.startsAt)}`}
                >
                  <View style={styles.rowLeft}>
                    <Text style={styles.rowService} numberOfLines={1} ellipsizeMode="tail">
                      {a.serviceName}
                    </Text>
                    <Text style={styles.rowDate}>{formatDate(a.startsAt)}</Text>
                  </View>
                  <View style={styles.rowRight}>
                    {hasDetail ? (
                      <Ionicons
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={14}
                        color={ticketColors.ink3}
                        style={styles.chevron}
                      />
                    ) : null}
                    <View
                      style={[styles.statusPill, { backgroundColor: STATUS_COLOR[a.status] + '22' }]}
                    >
                      <Text style={[styles.statusText, { color: STATUS_COLOR[a.status] }]}>
                        {STATUS_LABEL[a.status]}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
                {isExpanded ? (
                  <View style={styles.detailContainer} testID={`customers.detail.appointment.${a.id}.detail`}>
                    {a.resourceName ? (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Profesional:</Text>
                        <Text style={styles.detailValue}>{a.resourceName}</Text>
                      </View>
                    ) : null}
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>Duración:</Text>
                      <Text style={styles.detailValue}>{formatDuration(a.startsAt, a.endsAt)}</Text>
                    </View>
                    {a.paymentMode && a.paymentMode !== 'none' ? (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Pago:</Text>
                        <Text style={styles.detailValue}>
                          {PAYMENT_MODE_LABEL[a.paymentMode] ?? a.paymentMode}
                          {a.paymentStatus ? ` · ${PAYMENT_STATUS_LABEL[a.paymentStatus] ?? a.paymentStatus}` : ''}
                        </Text>
                      </View>
                    ) : null}
                    {a.notes ? (
                      <View style={styles.detailNotesRow}>
                        <Text style={styles.detailLabel}>Notas:</Text>
                        <Text style={styles.detailNotesValue}>{a.notes}</Text>
                      </View>
                    ) : null}
                    {a.saleId && onViewSale ? (
                      <TouchableOpacity
                        style={styles.viewSaleLink}
                        onPress={() => onViewSale(a.saleId!)}
                        accessibilityRole="button"
                        accessibilityLabel="Ver venta asociada"
                      >
                        <Ionicons name="receipt-outline" size={13} color="#B45309" />
                        <Text style={styles.viewSaleText}>Ver venta</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}
          {branchFilter && filteredAppointments.length === 0 ? (
            <Text style={styles.emptyText}>Sin turnos en esta sucursal.</Text>
          ) : null}
        </View>
      )}

      {(apptHasMore || apptPage > 0) && onLoadApptPage ? (
        <View style={styles.pagerRow} testID="customers.detail.appointments-pager">
          <TouchableOpacity
            style={[styles.pagerBtn, apptPage === 0 && styles.pagerBtnDisabled]}
            onPress={() => apptPage > 0 && onLoadApptPage(apptPage - 1)}
            disabled={apptPage === 0 || loading}
            accessibilityRole="button"
            accessibilityLabel="Página anterior"
          >
            <Ionicons name="chevron-back" size={16} color={apptPage === 0 ? ticketColors.ink3 : ticketColors.ink} />
            <Text style={[styles.pagerText, apptPage === 0 && styles.pagerTextDisabled]}>Anterior</Text>
          </TouchableOpacity>
          <Text style={styles.pagerInfo}>Pág. {apptPage + 1}</Text>
          <TouchableOpacity
            style={[styles.pagerBtn, !apptHasMore && styles.pagerBtnDisabled]}
            onPress={() => apptHasMore && onLoadApptPage(apptPage + 1)}
            disabled={!apptHasMore || loading}
            accessibilityRole="button"
            accessibilityLabel="Página siguiente"
          >
            <Text style={[styles.pagerText, !apptHasMore && styles.pagerTextDisabled]}>Siguiente</Text>
            <Ionicons name="chevron-forward" size={16} color={!apptHasMore ? ticketColors.ink3 : ticketColors.ink} />
          </TouchableOpacity>
        </View>
      ) : null}
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: {
    padding: spacing.md,
  },
  panelMobile: {
    padding: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  summaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.border,
  },
  salesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.border,
  },
  countBadge: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 4,
  },
  countText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink3,
  },
  summaryBox: {
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  summaryLine: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 20,
    marginBottom: 2,
  },
  summaryBold: {
    fontWeight: '700',
    color: ticketColors.ink,
  },
  summaryMuted: {
    color: ticketColors.ink3,
    fontSize: 12,
  },
  list: {
    gap: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  rowLeft: {
    flex: 1,
    paddingRight: spacing.sm,
  },
  rowService: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink,
  },
  rowDate: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  statusPill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 14,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
  },
  moreText: {
    fontSize: 12,
    color: ticketColors.ink3,
    paddingTop: spacing.xs,
    textAlign: 'center',
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    marginTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  pagerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    minHeight: 44,
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  pagerTextDisabled: {
    color: ticketColors.ink3,
  },
  pagerInfo: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  branchFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  branchChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    minHeight: 44,
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    maxWidth: 140,
  },
  branchChipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold + '22',
  },
  branchChipText: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  branchChipTextActive: {
    color: ticketColors.gold,
    fontWeight: '800',
  },
  rowContainer: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  rowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  chevron: {
    marginRight: 2,
  },
  detailContainer: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: ticketColors.paper2,
    gap: spacing.xs,
  },
  detailRow: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  detailNotesRow: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  detailValue: {
    fontSize: 12,
    color: ticketColors.ink,
    flex: 1,
  },
  detailNotesValue: {
    fontSize: 12,
    color: ticketColors.ink,
    flex: 1,
    fontStyle: 'italic',
  },
  viewSaleLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
    alignSelf: 'flex-start',
  },
  viewSaleText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#B45309',
  },
  windowsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  windowItem: {
    flex: 1,
    alignItems: 'flex-start',
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  windowValue: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  windowLabel: {
    fontSize: 10,
    color: ticketColors.ink3,
    marginTop: 2,
  },
});
